import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE } from '../constants/layers';
import type { TargetFunction, TargetType } from '../store/reducers/workspace.reducer';

/**
 * Individual photometric targets a layer can require instead of an EN 13201 class.
 *
 * Only quantities the lighting calculation evaluates are offered. Threshold increment (fTI),
 * longitudinal uniformity (Ul), wet uniformity (Uow) and the edge illuminance ratio (REI) are
 * part of the M-class tables but do not change the result, so they are not available here.
 *
 * A combination is accepted when it describes one lighting level without contradicting itself:
 * - at least one photometric quantity (uniformity alone sets no level)
 * - a quantity is either an average or an exact value, not both
 * - an exact value cannot also carry a minimum or a uniformity; a minimum may sit under an average, and not above it
 * - minimum uniformity needs exactly one average, so it is clear what it ratios
 */

export interface PhotometricTarget {
  function: TargetFunction;
  type: TargetType;
  value: number;
}

type TargetGroup = 'horizontal' | 'luminance' | 'hemispherical' | 'vertical' | 'semicylindrical' | 'uniformity';

export interface TargetOption {
  id: string;
  function: TargetFunction;
  type: TargetType;
  group: TargetGroup;
  /** Shown when the row is first switched on. */
  defaultValue: number;
  min: number;
  max: number;
  step: number;
}

export const TARGET_OPTIONS: readonly TargetOption[] = [
  {
    id: 'E-AVERAGE',
    function: 'E',
    type: 'AVERAGE',
    group: 'horizontal',
    defaultValue: 10,
    min: 0.1,
    max: 500,
    step: 0.5,
  },
  { id: 'E-EXACT', function: 'E', type: 'EXACT', group: 'horizontal', defaultValue: 10, min: 0.1, max: 500, step: 0.5 },
  { id: 'E-MIN', function: 'E', type: 'MIN', group: 'horizontal', defaultValue: 2, min: 0.1, max: 500, step: 0.1 },
  {
    id: 'L-AVERAGE',
    function: 'L',
    type: 'AVERAGE',
    group: 'luminance',
    defaultValue: 1,
    min: 0.05,
    max: 20,
    step: 0.05,
  },
  { id: 'L-EXACT', function: 'L', type: 'EXACT', group: 'luminance', defaultValue: 1, min: 0.05, max: 20, step: 0.05 },
  { id: 'L-MIN', function: 'L', type: 'MIN', group: 'luminance', defaultValue: 0.3, min: 0.05, max: 20, step: 0.05 },
  {
    id: 'Ehs-AVERAGE',
    function: 'Ehs',
    type: 'AVERAGE',
    group: 'hemispherical',
    defaultValue: 2.5,
    min: 0.1,
    max: 100,
    step: 0.1,
  },
  {
    id: 'Ehs-EXACT',
    function: 'Ehs',
    type: 'EXACT',
    group: 'hemispherical',
    defaultValue: 2.5,
    min: 0.1,
    max: 100,
    step: 0.1,
  },
  {
    id: 'Ehs-MIN',
    function: 'Ehs',
    type: 'MIN',
    group: 'hemispherical',
    defaultValue: 0.5,
    min: 0.1,
    max: 100,
    step: 0.1,
  },
  {
    id: 'Ev-AVERAGE',
    function: 'Ev',
    type: 'AVERAGE',
    group: 'vertical',
    defaultValue: 5,
    min: 0.1,
    max: 100,
    step: 0.1,
  },
  { id: 'Ev-MIN', function: 'Ev', type: 'MIN', group: 'vertical', defaultValue: 3, min: 0.1, max: 100, step: 0.1 },
  {
    id: 'Esc-AVERAGE',
    function: 'Esc',
    type: 'AVERAGE',
    group: 'semicylindrical',
    defaultValue: 3,
    min: 0.1,
    max: 100,
    step: 0.1,
  },
  {
    id: 'Esc-MIN',
    function: 'Esc',
    type: 'MIN',
    group: 'semicylindrical',
    defaultValue: 2,
    min: 0.1,
    max: 100,
    step: 0.1,
  },
  { id: 'Uo-MIN', function: 'Uo', type: 'MIN', group: 'uniformity', defaultValue: 0.4, min: 0.05, max: 1, step: 0.05 },
];

const PHOTOMETRIC: ReadonlySet<TargetFunction> = new Set(['E', 'L', 'Ehs', 'Ev', 'Esc']);
const EXACT_FUNCTIONS: ReadonlySet<TargetFunction> = new Set(['E', 'L', 'Ehs']);

type TargetIssueCode =
  | 'empty'
  | 'no-level'
  | 'unsupported'
  | 'duplicate'
  | 'average-and-exact'
  | 'exact-and-min'
  | 'exact-and-uniformity'
  | 'min-above-level'
  | 'uniformity-needs-one-level'
  | 'bad-value';

interface TargetIssue {
  code: TargetIssueCode;
  function?: TargetFunction;
}

/** Empty is not a target set — the caller should clear the requirement instead of saving it. */
export function validatePhotometricTargets(targets: readonly PhotometricTarget[]): TargetIssue | null {
  if (!targets.length) return { code: 'empty' };

  const seen = new Set<string>();
  const photometric = new Set<TargetFunction>();
  const level = new Set<TargetFunction>();
  let uniformity = false;

  for (const target of targets) {
    const key = `${target.function}:${target.type}`;
    if (seen.has(key)) return { code: 'duplicate', function: target.function };
    seen.add(key);

    if (target.function === 'Uo') {
      if (target.type !== 'MIN' || !(target.value > 0) || target.value > 1) {
        return { code: 'bad-value', function: 'Uo' };
      }
      uniformity = true;
      continue;
    }

    if (!PHOTOMETRIC.has(target.function)) return { code: 'unsupported', function: target.function };
    if (target.type === 'EXACT') {
      if (!EXACT_FUNCTIONS.has(target.function)) return { code: 'unsupported', function: target.function };
    } else if (target.type !== 'AVERAGE' && target.type !== 'MIN') {
      return { code: 'unsupported', function: target.function };
    }
    if (!(target.value > 0) || !Number.isFinite(target.value)) {
      return { code: 'bad-value', function: target.function };
    }
    photometric.add(target.function);
    if (target.type === 'AVERAGE' || target.type === 'EXACT') level.add(target.function);
  }

  if (photometric.size === 0) return { code: 'no-level' };

  for (const fn of photometric) {
    const average = targets.find((t) => t.function === fn && t.type === 'AVERAGE');
    const exact = targets.find((t) => t.function === fn && t.type === 'EXACT');
    if (average && exact) return { code: 'average-and-exact', function: fn };
    const minimum = targets.find((t) => t.function === fn && t.type === 'MIN');
    if (exact && minimum) return { code: 'exact-and-min', function: fn };
    if (average && minimum && minimum.value > average.value) {
      return { code: 'min-above-level', function: fn };
    }
  }

  if (uniformity && targets.some((target) => target.type === 'EXACT' && PHOTOMETRIC.has(target.function))) {
    return { code: 'exact-and-uniformity' };
  }
  if (uniformity && level.size !== 1) return { code: 'uniformity-needs-one-level' };
  return null;
}

interface LayerRequirementSource {
  isIlluminated?: boolean;
  selectedNorm?: string | null;
  customTargets?: PhotometricTarget[] | null;
}

interface SavedLayerRequirement {
  normId: number | null;
  normName: string;
  targets?: PhotometricTarget[];
}

/** What to persist for a layer. Custom targets win over a catalog class name. */
export function requirementForSave(
  layer: LayerRequirementSource,
  norms: readonly { id: number; name: string }[] | null | undefined,
): SavedLayerRequirement {
  // Illumination is explicit in the workspace. Callers that only carry a selected class
  // (and leave the flag unset) still count as lit, matching the previous save payload.
  const explicitlyOff = layer.isIlluminated === false;
  const hasCustom = !explicitlyOff && !!layer.customTargets?.length;
  const hasCatalog =
    !explicitlyOff &&
    !!layer.selectedNorm &&
    layer.selectedNorm !== LAYER_NORM_NONE &&
    layer.selectedNorm !== LAYER_NORM_CUSTOM;
  const illuminated = !explicitlyOff && (layer.isIlluminated === true || hasCatalog || hasCustom);

  if (illuminated && layer.customTargets?.length) {
    return { normId: null, normName: '', targets: layer.customTargets };
  }
  const name = illuminated && hasCatalog ? layer.selectedNorm! : '';
  const match = name && norms ? norms.find((norm) => norm.name === name) : undefined;
  return { normId: match?.id ?? null, normName: name };
}

interface MeshRequirementSource {
  normId?: number | null;
  targets?: PhotometricTarget[] | null;
  isIlluminated?: boolean;
}

/** Restore the workspace fields from a saved mesh. */
export function requirementFromMesh(
  mesh: MeshRequirementSource,
  norms: readonly { id: number; name: string }[],
): { isIlluminated: boolean; selectedNorm: string | null; customTargets: PhotometricTarget[] | null } {
  if (mesh.targets?.length) {
    return { isIlluminated: true, selectedNorm: LAYER_NORM_CUSTOM, customTargets: mesh.targets };
  }
  if (mesh.normId != null) {
    const norm = norms.find((item) => item.id === mesh.normId);
    return {
      isIlluminated: mesh.isIlluminated !== undefined ? mesh.isIlluminated : true,
      selectedNorm: norm?.name ?? 'Unknown',
      customTargets: null,
    };
  }
  return {
    isIlluminated: mesh.isIlluminated ?? false,
    selectedNorm: LAYER_NORM_NONE,
    customTargets: null,
  };
}

const FUNCTION_LABEL: Record<string, string> = {
  E: 'horizontal illuminance',
  L: 'luminance',
  Ehs: 'hemispherical illuminance',
  Ev: 'vertical illuminance',
  Esc: 'semi-cylindrical illuminance',
  Uo: 'uniformity',
};

const TYPE_LABEL: Record<string, string> = {
  AVERAGE: 'average',
  EXACT: 'exact',
  MIN: 'min',
};

const UNIT: Record<string, string> = {
  E: 'lx',
  Ev: 'lx',
  Esc: 'lx',
  Ehs: 'lx',
  L: 'cd/m²',
};

/** Compact tooltip, e.g. "horizontal illuminance average 10 lx, uniformity min 0.40". */
export function describeTargets(targets: readonly PhotometricTarget[]): string {
  return targets
    .map((target) => {
      const unit = UNIT[target.function];
      const value = target.function === 'Uo' ? target.value.toFixed(2) : String(target.value);
      return `${FUNCTION_LABEL[target.function] ?? target.function} ${TYPE_LABEL[target.type] ?? target.type} ${value}${unit ? ` ${unit}` : ''}`;
    })
    .join(', ');
}
